import React, {
    useEffect,
    useMemo,
    useState,
    useLayoutEffect,
    useRef,
    useCallback,
} from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { useNavigate } from "react-router-dom";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import {
    Landmark,
    Search,
    MoreVertical,
    LayoutGrid,
    List,
    Filter,
    Eye,
    Plus,
    Edit2,
    EyeOff,
    Clock3,
    RefreshCw,
    CheckCircle2,
    CircleAlert,
    XCircle,
    PauseCircle,
    Ban,
    FileClock,
    ShieldCheck,
    User,
    CalendarDays,
    Building2,
    CreditCard,
    MapPin,
    Mail,
    Phone,
    FileText,
    History,
    ArrowRight,
    FileCheck,
    BadgeCheck,
} from "lucide-react";

import { eftEnrollmentApi } from "../../api/eftEnrollmentApi";
import { isApiConfigured } from "../../api/axiosClient";
import { LoadingSpinner, ErrorCard } from "../../components/LoadingSpinner";
import { CardsPagination } from "../../components/CardsPagination";
import { useToast } from "../../components/Toast";
import { process } from "@progress/kendo-data-query";
import { useAuth } from '../../auth/AuthContext';

import { usePermission } from '../../hooks/usePermission';

/* 
   CONSTANTS
*/

const STATUS_OPTIONS = [
    "Submitted",
    "Under Review",
    "Pending Information",
    "Approved",
    "Rejected",
    "Active",
    "Suspended",
    "Terminated",
];

const STATUS_META = {
    Submitted: {
        bg: "#DBEAFE",
        color: "#2563EB",
        border: "rgba(37,99,235,0.22)",
        icon: FileClock,
    },
    "Under Review": {
        bg: "#EDE9FE",
        color: "#7C3AED",
        border: "rgba(124,58,237,0.22)",
        icon: Clock3,
    },
    "Pending Information": {
        bg: "#FEF3C7",
        color: "#B45309",
        border: "rgba(180,83,9,0.22)",
        icon: CircleAlert,
    },
    Approved: {
        bg: "#D1FAE5",
        color: "#059669",
        border: "rgba(5,150,105,0.22)",
        icon: CheckCircle2,
    },
    Rejected: {
        bg: "#FEE2E2",
        color: "#DC2626",
        border: "rgba(220,38,38,0.22)",
        icon: XCircle,
    },
    Active: {
        bg: "#CCFBF1",
        color: "#0F766E",
        border: "rgba(15,118,110,0.22)",
        icon: ShieldCheck,
    },
    Suspended: {
        bg: "#F3F4F6",
        color: "#6B7280",
        border: "rgba(107,114,128,0.22)",
        icon: PauseCircle,
    },
    Terminated: {
        bg: "#FEE2E2",
        color: "#991B1B",
        border: "rgba(153,27,27,0.22)",
        icon: Ban,
    },
};

/* 
   HELPERS
*/

const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    //return date.toLocaleDateString('en-US', {
    //    month: 'short',
    //    day: 'numeric',
    //    year: 'numeric',
    //});

    return date.toLocaleDateString("en-US", {
        //   timeZone: 'UTC',
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
    });
};

const formatDateTime = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    //return date.toLocaleString('en-US', {
    //    month: 'short',
    //    day: 'numeric',
    //    year: 'numeric',
    //    hour: 'numeric',
    //    minute: '2-digit',
    //});

    return date.toLocaleString("en-US", {
        //timeZone: 'UTC',
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

const relativeDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    const diff = Date.now() - date.getTime();

    if (diff < 0) return "upcoming";

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 2) return "yesterday";
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;

    return formatDate(value);
};

const maskAccountNumber = (accountNumber) => {
    if (!accountNumber) return "—";

    const value = String(accountNumber);

    if (value.length <= 4) {
        return "••••";
    }

    return `•••• •••• ${value.slice(-4)}`;
};

const getEnrollmentName = (item) => {
    return item.legalName || item.LegalName || "Unknown Provider";
};

const getEnrollmentId = (item) => {
    return item.id || item.EFTEnrollmentId;
};

const getConfirmationNumber = (item) => {
    return item.confirmationNumber || "";
};

const StatusPill = ({ status }) => {
    const config = STATUS_META[status] || {
        bg: "rgba(156,163,175,0.12)",
        color: "#6B7280",
        border: "rgba(156,163,175,0.25)",
    };

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px 4px 8px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                background: config.bg,
                color: config.color,
                border: `1px solid ${config.border}`,
                whiteSpace: "nowrap",
            }}
        >
            <span
                style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: config.color,
                }}
            />
            {status || "Unknown"}
        </span>
    );
};

/* ============================================================
   STATUS BADGE
============================================================ */

const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || {
        bg: "#F3F4F6",
        color: "#6B7280",
        border: "rgba(107,114,128,0.2)",
        icon: CircleAlert,
    };

    const Icon = meta.icon;

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 10px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                background: meta.bg,
                color: meta.color,
                border: `1px solid ${meta.border}`,
                whiteSpace: "nowrap",
            }}
        >
            <Icon size={12} strokeWidth={2.4} />
            {status || "Unknown"}
        </span>
    );
};

/*
   ACCOUNT NUMBER
*/

const AccountNumber = ({
    maskedAccountNumber,
    revealed,
    onToggle,
    loading,
}) => {
    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                minWidth: 115,
            }}
        >
            <span
                style={{
                    fontFamily: revealed
                        ? "ui-monospace, SFMono-Regular, Menlo, monospace"
                        : "inherit",
                    fontSize: 12,
                    color: "#4B5563",
                    letterSpacing: revealed ? 0.5 : 1,
                }}
            >
                {revealed
                    ? maskedAccountNumber?.raw || "—"
                    : maskedAccountNumber?.masked || "—"}
            </span>

            <button
                type="button"
                onClick={onToggle}
                disabled={loading}
                title={revealed ? "Hide account number" : "Show account number"}
                aria-label={
                    revealed ? "Hide bank account number" : "Show bank account number"
                }
                style={{
                    width: 25,
                    height: 25,
                    borderRadius: 7,
                    border: "1px solid #E8ECF0",
                    background: "#F8FAFB",
                    color: "#6B7280",
                    cursor: loading ? "not-allowed" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    flexShrink: 0,
                    transition: "all .15s",
                }}
                onMouseEnter={(e) => {
                    if (!loading) {
                        e.currentTarget.style.color = "#0D9488";
                        e.currentTarget.style.borderColor = "rgba(13,148,136,.3)";
                        e.currentTarget.style.background = "rgba(13,148,136,.06)";
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#6B7280";
                    e.currentTarget.style.borderColor = "#E8ECF0";
                    e.currentTarget.style.background = "#F8FAFB";
                }}
            >
                {revealed ? (
                    <EyeOff size={13} strokeWidth={2.2} />
                ) : (
                    <Eye size={13} strokeWidth={2.2} />
                )}
            </button>
        </div>
    );
};

/* 
   ENROLLMENT CARD
*/

const EnrollmentCard = ({
    enrollment,
    onChangeStatus,
    onHistory,
    onRevealAccount,
    onView,
    onEdit,
    accessPermissions
}) => {
    const [showAccount, setShowAccount] = useState(false);
    const [accountLoading, setAccountLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const status = enrollment.status;

    const navigate = useNavigate();
    //const handleEftAction = (id, mode, userId) => {
    //    navigate(`/forms/eft-enrollment?id=${id}&mode=${mode}&uid=${userId}`);

    //};

    const handleAccountToggle = async () => {
        if (showAccount) {
            setShowAccount(false);
            return;
        }

        setAccountLoading(true);

        try {
            const response = await onRevealAccount(enrollment);

            if (response) {
                setShowAccount(true);
            }
        } finally {
            setAccountLoading(false);
        }
    };

    return (
        <>
            
            <div className="card-scroll-container">
                <div
                    className="responsive-card-grid"
                    style={{
                        background: "#fff",
                        border: "1px solid #E8ECF0",
                        borderRadius: 16,
                        padding: 18,
                        position: "relative",
                        transition: "all .2s",
                        boxShadow: "0 1px 2px rgba(0,0,0,.03)",

                    }}
                    data-cy="enrollmentCard"
                    onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#CBD5E1";
                        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.06)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#E8ECF0";
                        e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,.03)";
                        e.currentTarget.style.transform = "translateY(0)";
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 12,
                            marginBottom: 14,
                        }}
                    >
                        <div
                            style={{
                                width: 46,
                                height: 46,
                                borderRadius: 13,
                                background: "rgba(13,148,136,.09)",
                                color: "#0D9488",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                border: "1px solid rgba(13,148,136,.15)",
                            }}
                        >
                            <Landmark size={22} strokeWidth={2} />
                        </div>

                        <div
                            style={{
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: "#1A1D21",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {getEnrollmentName(enrollment)}
                            </div>

                            <div
                                style={{
                                    marginTop: 3,
                                    fontSize: 11,
                                    color: "#9CA3AF",
                                    fontFamily: "monospace",
                                    display: "flex",
                                    gap: "20px",
                                }}
                            >
                                {/*<span>Enrollment #{getEnrollmentId(enrollment)}</span>*/}
                                {/*<span>Confirmation #{getConfirmationNumber(enrollment)}</span>*/}
                                <span>
                                    {enrollment?.registeredUserName && enrollment.registeredEmail && (
                                        <>
                                            @{enrollment.registeredUserName} {enrollment.registeredEmail}
                                        </>
                                    )}

                                </span>
                            </div>

                            <div
                                style={{
                                    marginTop: 7,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    flexWrap: "wrap",
                                }}
                            >
                                <StatusBadge status={status} />

                                {enrollment.reason && (
                                    <span
                                        style={{
                                            padding: "4px 8px",
                                            borderRadius: 999,
                                            background: "#F8FAFC",
                                            border: "1px solid #E8ECF0",
                                            color: "#64748B",
                                            fontSize: 10.5,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {enrollment.reason}
                                    </span>
                                )}
                            </div>
                        </div>
                        {/* Menu */}
                        <div style={{ position: "relative" }}>
                            <button
                                onClick={() => setMenuOpen(!menuOpen)}
                                data-cy="cardMenuButton"
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    border: "none",
                                    background: menuOpen ? "#F3F4F6" : "transparent",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#6B7280",
                                }}
                                aria-label="More options"
                            >
                                <MoreVertical size={16} />
                            </button>

                            {menuOpen && (
                                <>
                                    <div
                                        style={{
                                            position: "fixed",
                                            inset: 0,
                                            zIndex: 10,
                                        }}
                                        onClick={() => setMenuOpen(false)}
                                    />

                                    <div
                                        style={{
                                            position: "absolute",
                                            right: 0,
                                            top: 36,
                                            zIndex: 11,
                                            background: "#fff",
                                            border: "1px solid #E8ECF0",
                                            borderRadius: 12,
                                            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                                            minWidth: 190,
                                            padding: 6,
                                            overflow: "hidden",
                                        }}
                                    >
                                        {accessPermissions.canViewEFTEnrollments && (
                                            <>
                                                <MenuItem
                                                    icon={Eye}

                                                    label="View enrollment"
                                                    onClick={() => {
                                                        setMenuOpen(false);
                                                        onView(enrollment);
                                                    }}
                                                />
                                            </>
                                        )}
                                        {accessPermissions.canEditEFTEnrollment && (
                                            <>
                                                <MenuItem
                                                    icon={Edit2}
                                                    label="Edit enrollment"
                                                    color="#0D9488"
                                                    onClick={() => {
                                                        setMenuOpen(false);
                                                        //handleEftAction(enrollment.tin, "change", enrollment.userId);
                                                        onEdit(enrollment.userId, "change", enrollment.tin);
                                                    }}
                                                />
                                            </>
                                        )}
                                        {accessPermissions.canViewEFTStatusHistory && (
                                            <>
                                                <MenuItem
                                                    icon={History}
                                                    label="Status history"
                                                    color="#7C3AED"
                                                    onClick={() => {
                                                        setMenuOpen(false);
                                                        onHistory(enrollment);
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Provider information */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 9,
                            marginBottom: 14,
                        }}
                    >
                        <InfoRow
                            icon={BadgeCheck}
                            label="Enrollment #"
                            value={getEnrollmentId(enrollment)}
                        />

                        <InfoRow
                            icon={FileCheck}
                            label="Confirmation #"
                            value={getConfirmationNumber(enrollment)}
                        />
                        <InfoRow icon={User} label="Contact" value={enrollment.contactName} />

                        <InfoRow icon={Mail} label="Email" value={enrollment.email} />

                        <InfoRow icon={FileText} label="TIN" value={enrollment.tin} mono />

                        <InfoRow icon={CreditCard} label="NPI" value={enrollment.npi} mono />

                        <InfoRow icon={Building2} label="Bank" value={enrollment.bankName} />

                        <InfoRow
                            icon={CreditCard}
                            label="Account"
                            value={
                                <AccountNumber
                                    maskedAccountNumber={{
                                        masked: enrollment.maskedAccountNumber,
                                        raw: enrollment.rawAccountNumber,
                                    }}
                                    revealed={showAccount}
                                    onToggle={handleAccountToggle}
                                    loading={accountLoading}
                                />
                            }
                        />

                        <InfoRow
                            icon={CalendarDays}
                            label="Submission"
                            value={formatDate(enrollment.submissionDate)}
                        />

                        <InfoRow
                            icon={CalendarDays}
                            label="Effective"
                            value={formatDate(enrollment.effectiveDate)}
                        />
                    </div>

                    {/* Admin note */}
                    {enrollment.adminNote && (
                        <div
                            style={{
                                padding: "9px 11px",
                                borderRadius: 9,
                                background: "#F8FAFC",
                                border: "1px solid #EEF2F7",
                                marginBottom: 14,
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: "#64748B",
                                    marginBottom: 4,
                                }}
                            >
                                <FileText size={12} />
                                Admin Note
                            </div>

                            <div
                                style={{
                                    fontSize: 12,
                                    color: "#475569",
                                    lineHeight: 1.45,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                }}
                            >
                                {enrollment.adminNote}
                            </div>
                        </div>
                    )}

                    {/*User registered information  */}

                    {/*{(enrollment.registeredUserName ||*/}
                    {/*  enrollment.registeredFirstName ||*/}
                    {/*  enrollment.registeredLastName ||*/}
                    {/*  enrollment.registeredEmail) && (*/}
                    {/*  <div*/}
                    {/*    style={{*/}
                    {/*      padding: "10px 12px",*/}
                    {/*      borderRadius: 10,*/}
                    {/*      background: "#F8FAFC",*/}
                    {/*      border: "1px solid #E2E8F0",*/}
                    {/*      marginBottom: 14,*/}
                    {/*    }}*/}
                    {/*  >*/}
                    {/*    */}
                    {/* Header */}
                    {/*    <div*/}
                    {/*      style={{*/}
                    {/*        display: "flex",*/}
                    {/*        alignItems: "center",*/}
                    {/*        gap: 5,*/}
                    {/*        fontSize: 10.5,*/}
                    {/*        fontWeight: 700,*/}
                    {/*        color: "#64748B",*/}
                    {/*        marginBottom: 7,*/}
                    {/*      }}*/}
                    {/*    >*/}
                    {/*      <User size={12} />*/}
                    {/*      Provider Admin User*/}
                    {/*    </div>*/}

                    {/*    */}
                    {/* Username */}
                    {/*    {enrollment.registeredUserName && (*/}
                    {/*      <div*/}
                    {/*        style={{*/}
                    {/*          fontSize: 12,*/}
                    {/*          fontWeight: 600,*/}
                    {/*          color: "#334155",*/}
                    {/*          marginBottom: 2,*/}
                    {/*          overflow: "hidden",*/}
                    {/*          textOverflow: "ellipsis",*/}
                    {/*          whiteSpace: "nowrap",*/}
                    {/*        }}*/}
                    {/*      >*/}
                    {/*        @{enrollment.registeredUserName}*/}
                    {/*      </div>*/}
                    {/*    )}*/}

                    {/*    */}
                    {/* Full Name */}
                    {/*    {(enrollment.registeredFirstName ||*/}
                    {/*      enrollment.registeredLastName) && (*/}
                    {/*      <div*/}
                    {/*        style={{*/}
                    {/*          fontSize: 11.5,*/}
                    {/*          color: "#64748B",*/}
                    {/*          marginBottom: enrollment.registeredEmail ? 4 : 0,*/}
                    {/*          overflow: "hidden",*/}
                    {/*          textOverflow: "ellipsis",*/}
                    {/*          whiteSpace: "nowrap",*/}
                    {/*        }}*/}
                    {/*      >*/}
                    {/*        {[enrollment.registeredFirstName, enrollment.registeredLastName]*/}
                    {/*          .filter(Boolean)*/}
                    {/*          .join(" ")}*/}
                    {/*      </div>*/}
                    {/*    )}*/}

                    {/*    */}
                    {/* Email */}
                    {/*    {enrollment.registeredEmail && (*/}
                    {/*      <div*/}
                    {/*        title={enrollment.registeredEmail}*/}
                    {/*        style={{*/}
                    {/*          fontSize: 10.5,*/}
                    {/*          color: "#94A3B8",*/}
                    {/*          overflow: "hidden",*/}
                    {/*          textOverflow: "ellipsis",*/}
                    {/*          whiteSpace: "nowrap",*/}
                    {/*        }}*/}
                    {/*      >*/}
                    {/*        {enrollment.registeredEmail}*/}
                    {/*      </div>*/}
                    {/*    )}*/}
                    {/*  </div>*/}
                    {/*)}*/}

                    {/* Footer */}
                    <div
                        style={{
                            borderTop: "1px solid #F1F5F9",
                            paddingTop: 12,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                        }}
                    >
                        <div
                            style={{
                                fontSize: 11,
                                color: "#9CA3AF",
                            }}
                        >
                            Submitted {relativeDate(enrollment.submissionDate)}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: 6,
                            }}
                        >
                            {accessPermissions.canViewEFTEnrollments && (
                                <>
                                    <button
                                        onClick={() => onView(enrollment)}
                                        data-cy="cardViewButton"
                                        name="cardViewButton"
                                        style={{
                                            padding: "6px 12px",
                                            borderRadius: 8,
                                            border: "1px solid rgba(13,148,136,0.2)",
                                            background: "rgba(13,148,136,0.08)",
                                            color: "#0D9488",
                                            fontSize: 12,
                                            fontWeight: 700,
                                            cursor: "pointer",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 5,
                                            fontFamily: "inherit",
                                        }}
                                    >
                                        <Eye size={12} />
                                        View
                                    </button>
                                </>
                            )}

                            {accessPermissions.canViewEFTStatusHistory && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => onHistory(enrollment)}
                                        style={smallActionButton("#7C3AED")}
                                        data-cy="cardHistoryButton"
                                    >
                                        <History size={12} strokeWidth={2.4} />
                                        History
                                    </button>
                                </>
                            )}
                            {accessPermissions.canUpdateEFTStatus && (
                                <>
                                    <button
                                        type="button"
                                        data-cy="cardStatusButton"
                                        onClick={() => onChangeStatus(enrollment)}
                                        style={smallActionButton("#0D9488")}
                                    >
                                        <RefreshCw size={12} strokeWidth={2.4} />
                                        Status
                                    </button>
                                </>
                            )}




                        </div>
                    </div>
                </div>
            </div>
            
        </>
        
    );
};

/* 
   INFO ROW
*/

const InfoRow = ({ icon: Icon, label, value, mono = false }) => (
    <div
        style={{
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 7,
        }}
    >
        <Icon size={13} color="#9CA3AF" strokeWidth={2} style={{ flexShrink: 0 }} />

        <div
            style={{
                minWidth: 0,
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    fontSize: 9.5,
                    color: "#9CA3AF",
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                    fontWeight: 700,
                }}
            >
                {label}
            </div>

            <div
                style={{
                    fontSize: 12,
                    color: "#475569",
                    marginTop: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontFamily: mono
                        ? "ui-monospace, SFMono-Regular, Menlo, monospace"
                        : "inherit",
                }}
            >
                {value || "—"}
            </div>
        </div>
    </div>
);

/*
   STATUS COUNT CARD
*/

const StatusCountCard = ({ label, count, status, active, onClick }) => {
    const meta = STATUS_META[status];
    const Icon = meta.icon;

    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                border: active ? `1.5px solid ${meta.color}` : "1px solid #E8ECF0",
                background: active ? `${meta.bg}` : "#fff",
                borderRadius: 14,
                minHeight: 104,
                padding: 14,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                fontFamily: "inherit",
                transition: "all .15s",
                boxShadow: active
                    ? `0 3px 12px ${meta.color}18`
                    : "0 1px 2px rgba(0,0,0,.02)",
            }}
        >
            <div
                style={{
                    width: 30,
                    height: 30,
                    borderRadius: 9,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: meta.bg,
                    color: meta.color,
                    marginBottom: 6,
                }}
            >
                <Icon size={16} strokeWidth={2.2} />
            </div>

            <div
                style={{
                    fontSize: 21,
                    lineHeight: 1,
                    fontWeight: 800,
                    color: "#1A1D21",
                }}
            >
                {count}
            </div>

            <div
                style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: "#6B7280",
                    marginTop: 5,
                    lineHeight: 1.2,
                }}
            >
                {label}
            </div>
        </button>
    );
};

/* 
   Menu Item
*/

const MenuItem = ({ icon: Icon, label, onClick, color = "#1A1D21" }) => (
    <button
        onClick={onClick}
        style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            padding: "9px 12px",
            borderRadius: 8,
            border: "none",
            background: "transparent",
            fontSize: 13,
            fontWeight: 600,
            color,
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "left",
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.background = "#F8FAFB";
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
        }}
    >
        <Icon size={15} strokeWidth={2} />
        {label}
    </button>
);

/* 
   MAIN COMPONENT
*/

const EFTEnrollmentListPage = () => {
    const EFT_LIST_STATE_KEY = "eftEnrollmentListState";

    const getInitialListState = () => {
        try {
            const saved = sessionStorage.getItem(EFT_LIST_STATE_KEY);

            if (!saved) {
                return null;
            }

            return JSON.parse(saved);
        } catch {
            return null;
        }
    };
    const toast = useToast();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { can, canAny, canAll } = usePermission();
    const [enrollments, setEnrollments] = useState([]);
    const [totalRecord, setTotalRecord] = useState(0);

    const savedListState = getInitialListState();

    const [isRestoringState, setIsRestoringState] = useState(true);

    //console.log("------------------------------------------------------");
    //console.log("savedListState : " + JSON.stringify(savedListState));
    //console.log("------------------------------------------------------");

    const [page, setPage] = useState({
        skip: 0,
        take: 10,
    });

    const [sort, setSort] = useState([]);

    const [searchText, setSearchText] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");

    const [viewMode, setViewMode] = useState("cards");

    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [gridLoading, setGridLoading] = useState(false);
    const [statusCounts, setStatusCounts] = useState({});
    const [error, setError] = useState(null);

    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== "undefined" ? window.innerWidth : 1024,
    );

    const [statusDialog, setStatusDialog] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const [statusNotes, setStatusNotes] = useState("");
    const [statusSaving, setStatusSaving] = useState(false);

    const [historyDialog, setHistoryDialog] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const [selectedEnrollment, setSelectedEnrollment] = useState(null);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);

    const [showProviderDialog, setShowProviderDialog] = useState(false);
    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [providerLoading, setProviderLoading] = useState(false);
    const [providerSearch, setProviderSearch] = useState("");
    const [accessPermissions, setAccessPermissions] = useState({});

    //const [page, setPage] = useState({
    //  skip: 0,
    //  take: 10,
    //});

    //const [sort, setSort] = useState([]);


    const searchTimeoutRef = useRef(null);

    /***********************Access Permissions ******************************/

    useEffect(() => {
        setAccessPermissions({
            canViewEFTEnrollments: can('superAdmin', 'eftEnrollments', 'viewEnrollmentsDetails'),
            canCreateEFTEnrollment: can('superAdmin', 'eftEnrollments', 'createEnrollment'),
            canEditEFTEnrollment: can('superAdmin', 'eftEnrollments', 'editEnrollment'),
            canViewEFTStatusHistory: can('superAdmin', 'eftEnrollments', 'viewStatusHistory'),
            canUpdateEFTStatus: can('superAdmin', 'eftEnrollments', 'updateStatus'),
        });
    }, [user]);
    /***********************Access Permissions ******************************/

    useEffect(() => {
        const savedState = sessionStorage.getItem(EFT_LIST_STATE_KEY);

        let initialSearchText = "";
        let initialFilterStatus = "All";
        let initialPage = {
            skip: 0,
            take: 10,
        };
        let initialSort = [];

        let initialTotalRecord = 0;
        let initialViewMode = "cards";

        if (savedState) {
            try {
                const parsed = JSON.parse(savedState);

                initialSearchText = parsed.searchText ?? "";
                initialFilterStatus = parsed.filterStatus ?? "All";
                initialTotalRecord = parsed.totalRecord ?? 0;
                initialViewMode = parsed.viewMode ?? "cards";
                initialPage = parsed.page ?? {
                    skip: 0,
                    take: 10,
                };
                initialSort = parsed.sort ?? [];

                setSearchText(initialSearchText);
                setFilterStatus(initialFilterStatus);
                setPage(initialPage);
                setSort(initialSort);
                setTotalRecord(initialTotalRecord);
                setViewMode(initialViewMode);
            } catch (error) {
                console.error("Failed to restore EFT list state:", error);
            }
        }

        //  alert("Make API CALL");
        getEnrollments({
            requestedSkip: initialPage.skip,
            requestedTake: initialPage.take,
            requestedSearchText: initialSearchText,
            requestedStatus: initialFilterStatus,
            requestedSort: initialSort,
        });

        sessionStorage.removeItem(EFT_LIST_STATE_KEY);
    }, []);

    //useEffect(() => {
    //    const savedState = sessionStorage.getItem(EFT_LIST_STATE_KEY);

    //    if (savedState) {
    //        try {
    //            const parsed = JSON.parse(savedState);

    //            setSearchText(parsed.searchText ?? "");
    //            setFilterStatus(parsed.filterStatus ?? "All");
    //            setPage(
    //                parsed.page ?? {
    //                    skip: 0,
    //                    take: 10,
    //                }
    //            );
    //            setSort(parsed.sort ?? []);
    //        } catch (error) {
    //            console.error("Failed to restore EFT list state:", error);
    //        }
    //    }

    //    setIsRestoringState(false);
    //}, []);

    //useEffect(() => {
    //    if (isRestoringState) {
    //        return;
    //    }

    //    alert("MAKE API CALL ");

    //    getEnrollments({
    //        requestedSkip: page.skip,
    //        requestedTake: page.take,
    //        requestedSearchText: searchText,
    //        requestedStatus: filterStatus,
    //        requestedSort: sort,
    //    });
    //}, [isRestoringState]);

    /*
      ============================================================
      RESPONSIVE
      ============================================================
      */
    useLayoutEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const styles = {
        overlay: {
            position: "fixed",
            inset: 0, // replaces top/left/width/height
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
        },
        gridOverlay: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
        },
    };

    const saveListState = () => {
        sessionStorage.setItem(
            EFT_LIST_STATE_KEY,
            JSON.stringify({
                searchText,
                filterStatus,
                page,
                sort,
                viewMode,
                totalRecord,
            }),
        );
    };

    /*
      ============================================================
      GET EFT ENROLLMENTS
      ============================================================
      
      This now calls the API with:
      status,
      searchText
      sortColumnName
      sortColumnDirection
      startIndex
      pageSize
      exportCsv
      */

    const getEnrollments = useCallback(
        async ({
            requestedSkip = page.skip,
            requestedTake = page.take,
            requestedSearchText = searchText,
            requestedStatus = filterStatus,
            requestedSort = sort,
        } = {}) => {
            try {
                setError(null);

                setGridLoading(true);

                const activeSort =
                    Array.isArray(requestedSort) && requestedSort.length > 0
                        ? requestedSort[0]
                        : null;

                const sortColumnName = activeSort?.field || "";

                const sortColumnDirection =
                    activeSort?.dir === "asc" || activeSort?.dir === "desc"
                        ? activeSort.dir
                        : "";

                const response = await eftEnrollmentApi.getEFTEnrollmentList({
                    searchText: requestedSearchText?.trim() || "",
                    status:
                        requestedStatus && requestedStatus !== "All" ? requestedStatus : "",
                    sortColumnName,
                    sortColumnDirection,
                    startIndex: requestedSkip || 0,
                    pageSize: requestedTake || 10,
                    exportCsv: false,
                });

                if (!response?.success) {
                    throw new Error(
                        response?.message || "Unable to load EFT enrollments.",
                    );
                }

                const apiData = Array.isArray(response.data) ? response.data : [];

                const mappedData = apiData.map((e) => ({
                    id: e.EFTEnrollmentId,
                    eftEnrollmentId: e.EFTEnrollmentId,

                    userId: e.UserId,

                    registeredUserName: e.RegisteredUserName || "",
                    registeredFirstName: e.RegisteredFirstName || "",
                    registeredLastName: e.RegisteredLastName || "",
                    registeredEmail: e.RegisteredEmail || "",

                    reason: e.Reason || "",

                    legalName: e.LegalName || "",
                    dba: e.DBA || "",

                    //tin: e.TIN || "",
                    //npi: e.NPI || "",

                    tin: e.TIN ? Number(e.TIN) : null,
                    npi: e.NPI ? Number(e.NPI) : null,

                    contactName: e.ContactName || "",
                    email: e.Email || "",

                    tradingPartnerId: e.TradingPartnerId || "",

                    telephone: e.Telephone || "",
                    fax: e.Fax || "",

                    streetAddress: e.StreetAddress || "",
                    city: e.City || "",
                    state: e.State || "",
                    zip: e.ZIP || "",

                    thirdPartyAgent: e.ThirdPartyAgent || false,
                    agentName: e.AgentName || "",
                    agentPhone: e.AgentPhone || "",
                    agentEmail: e.AgentEmail || "",

                    bankName: e.BankName || "",

                    routingNumber: e.RoutingNumber,
                    accountType: e.AccountType || "",
                    accountNumber: e.AccountNumber,


                    linkAccountTo: e.LinkAccountTo || "",
                    bankContactPhone: e.BankContactPhone || "",

                    effectiveDate: e.EffectiveDate || "",

                    authorizedSigner: e.AuthorizedSigner || "",
                    title: e.Title || "",
                    electronicSignature: e.ElectronicSignature || "",
                    signatureDate: e.SignatureDate || "",

                    attestation: e.Attestation || false,

                    status: e.Status || "Submitted",

                    confirmationNumber: e.ConfirmationNumber || "",

                    notes: e.Notes || "",
                    adminNote: e.AdminNote || "",

                    createdBy: e.CreatedBy || "",
                    createdDate: e.CreatedDate || "",

                    submissionDate: e.CreatedDate || "",

                    modifiedBy: e.ModifiedBy || null,
                    modifiedDate: e.ModifiedDate || null,

                    /*
                     * Keep these if your API/SP returns them.
                     */
                    maskedAccountNumber:
                        e.MaskedAccountNumber || maskAccountNumber(e.AccountNumber) || "",

                    rawAccountNumber: e.RawAccountNumber || null,

                    totalRecord: e.TotalRecord || 0,
                }));

                setEnrollments(mappedData);

                /*
                 * IMPORTANT:
                 * TotalRecord comes from COUNT(*) OVER()
                 * in the SQL stored procedure.
                 */
                setTotalRecord(Number(response.totalRecord) || 0);

                const counts = {};

                STATUS_OPTIONS.forEach((status) => {
                    counts[status] = 0;
                });

                (response.statusCounts || []).forEach((item) => {
                    const status = item.Status ?? item.status;
                    const count = item.StatusCount ?? item.statusCount;

                    if (status) {
                        counts[status] = Number(count) || 0;
                    }
                });
                //console.log("FULL API RESPONSE:", response);
                //console.log("response.statusCounts:", response?.statusCounts);
                //console.log("response.data:", response?.data);
                //console.log("STATUS COUNTS:", counts);

                setStatusCounts(counts);

                /*
                 * If API returns fewer records after filtering,
                 * make sure the current page remains valid.
                 */
                if (
                    mappedData.length === 0 &&
                    requestedSkip > 0 &&
                    Number(response.totalRecord || 0) > 0
                ) {
                    const lastPageSkip =
                        Math.floor((Number(response.totalRecord) - 1) / requestedTake) *
                        requestedTake;

                    if (lastPageSkip !== requestedSkip) {
                        setPage((prev) => ({
                            ...prev,
                            skip: lastPageSkip,
                        }));
                    }
                }
            } catch (err) {
                console.error("Failed to load EFT enrollments:", err);

                setError(err?.message || "Unable to load EFT enrollment information.");

                setEnrollments([]);
                setTotalRecord(0);
            } finally {
                setLoading(false);
                setPageLoading(false);
                setGridLoading(false);
            }
        },
        [page.skip, page.take, searchText, sort],
    );

    /*
      ============================================================
      INITIAL LOAD
      ============================================================
      */

    //useEffect(() => {
    //  getEnrollments({
    //    requestedSkip: 0,
    //    requestedTake: page.take,
    //    requestedSearchText: "",
    //    requestedSort: [],
    //  });

    //  // eslint-disable-next-line react-hooks/exhaustive-deps
    //}, []);


    //const handleSearchChange = (value) => {
    //  setSearchText(value);

    //  if (searchTimeoutRef.current) {
    //    clearTimeout(searchTimeoutRef.current);
    //  }

    //  searchTimeoutRef.current = setTimeout(() => {
    //    setPage((prev) => ({
    //      ...prev,
    //      skip: 0,
    //    }));

    //    getEnrollments({
    //      requestedSkip: 0,
    //      requestedTake: page.take,
    //      requestedSearchText: value,
    //      requestedSort: sort,
    //    });
    //  }, 400);
    //};


    /*
      ============================================================
      SEARCH
      ============================================================
      
      Do NOT call the API on every keystroke.
      
      Wait 400ms after the user stops typing.
      */

    const handleSearchChange = (value) => {
        setSearchText(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setPage((prev) => ({
                ...prev,
                skip: 0,
            }));

            getEnrollments({
                requestedSkip: 0,
                requestedTake: page.take,
                requestedSearchText: value,
                requestedStatus: filterStatus,
                requestedSort: sort,
            });
        }, 400);
    };
    /*
      ============================================================
      CLEAN SEARCH TIMER
      ============================================================
      */

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    /*
      ============================================================
      PAGINATION
      ============================================================
      
      Kendo sends:
      
      {
          skip,
          take
      }
      */

    //const handlePageChange = (event) => {
    //  const newPage = event?.page || event;

    //  const newSkip = Number(newPage?.skip) || 0;
    //  const newTake = Number(newPage?.take) || page.take || 10;

    //  setPage({
    //    skip: newSkip,
    //    take: newTake,
    //  });

    //  getEnrollments({
    //    requestedSkip: newSkip,
    //    requestedTake: newTake,
    //    requestedSearchText: searchText,
    //    requestedSort: sort,
    //  });
    //};

    const handlePageChange = (event) => {
        const newPage = event?.page || event;

        const newSkip = Number(newPage?.skip) || 0;
        const newTake = Number(newPage?.take) || page.take || 10;

        setPage({
            skip: newSkip,
            take: newTake,
        });

        getEnrollments({
            requestedSkip: newSkip,
            requestedTake: newTake,
            requestedSearchText: searchText,
            requestedStatus: filterStatus,
            requestedSort: sort,
        });
    };

    /*
      ============================================================
      SORTING
      ============================================================
      
  
    //const handleSortChange = (event) => {
    //  const incomingSort = Array.isArray(event?.sort) ? event.sort : [];
  
    //  const normalizedSort =
    //    incomingSort.length > 0
    //      ? [
    //          {
    //            field: incomingSort[0].field,
    //            dir: incomingSort[0].dir,
    //          },
    //        ]
    //      : [];
  
    //  setSort(normalizedSort);
  
    //  /*
    //   * Sorting starts from the first page.
    //   */
    //  setPage((prev) => ({
    //    ...prev,
    //    skip: 0,
    //  }));

    //  getEnrollments({
    //    requestedSkip: 0,
    //    requestedTake: page.take,
    //    requestedSearchText: searchText,
    //    requestedSort: normalizedSort,
    //  });
    //};

    const handleSortChange = (event) => {
        const incomingSort = Array.isArray(event?.sort) ? event.sort : [];

        const normalizedSort =
            incomingSort.length > 0
                ? [
                    {
                        field: incomingSort[0].field,
                        dir: incomingSort[0].dir,
                    },
                ]
                : [];

        setSort(normalizedSort);

        setPage((prev) => ({
            ...prev,
            skip: 0,
        }));

        getEnrollments({
            requestedSkip: 0,
            requestedTake: page.take,
            requestedSearchText: searchText,
            requestedStatus: filterStatus,
            requestedSort: normalizedSort,
        });
    };



    const displayedEnrollments = enrollments;



    useEffect(() => {

    }, [filterStatus]);
    const ProviderAvatar = ({ name = "", size = 44 }) => (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: size * 0.32,
                background:
                    "linear-gradient(135deg, rgba(13,148,136,0.14), rgba(13,148,136,0.05))",
                color: "#0D9488",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: size * 0.34,
                fontWeight: 700,
                border: "1.5px solid rgba(13,148,136,0.18)",
                flexShrink: 0,
            }}
        >
            {getProviderInitials(name)}
        </div>
    );
    const getProviderInitials = (name = "") => {
        const words = name.trim().split(/\s+/).filter(Boolean);

        if (!words.length) return "?";

        if (words.length === 1) {
            return words[0].substring(0, 2).toUpperCase();
        }

        return `${words[0][0]}${words[1][0]}`.toUpperCase();
    };

    /* 
         STATUS COUNTS
      */

    //const statusCounts = useMemo(() => {
    //  const counts = {};

    //  STATUS_OPTIONS.forEach((status) => {
    //    counts[status] = enrollments.filter((e) => e.status === status).length;
    //  });

    //  return counts;
    //}, [enrollments]);

    /* 
         CHANGE STATUS
      */

    const openStatusDialog = (enrollment) => {
        setStatusDialog(enrollment);

        setNewStatus(enrollment.status || "Submitted");

        setStatusNotes("");
    };

    const closeStatusDialog = () => {
        if (statusSaving) return;

        setStatusDialog(null);
        setNewStatus("");
        setStatusNotes("");
    };

    const handleStatusSave = async () => {
        if (!statusDialog) return;

        if (!newStatus) {
            toast.error("Please select a status.", {
                title: "Status required",
            });

            return;
        }

        if (newStatus === statusDialog.status && !statusNotes.trim()) {
            toast.info("Select a different status or enter notes.", {
                title: "No changes",
            });

            return;
        }

        const id = statusDialog.eftEnrollmentId || statusDialog.id;

        setStatusSaving(true);

        // alert('id : '+id+' newSatus : '+newStatus+' statusNotes : '+statusNotes);

        try {
            const response = await eftEnrollmentApi.updateEftEnrollmentStatus({
                id,
                newStatus,
                statusNotes: statusNotes.trim(),
            });

            //if (
            //    response?.success === false ||
            //    response?.Success === false
            //) {
            //    throw new Error(
            //        response?.message ||
            //        response?.Message ||
            //        'Failed to update enrollment status.'
            //    );
            //}

            if (response?.success === false || response?.Success === false) {
                const errors = response?.data?.errors || response?.Data?.errors;

                let message =
                    response?.message ||
                    response?.Message ||
                    "Failed to update enrollment status.";

                if (errors) {
                    const firstError = Object.values(errors)
                        .flat()
                        .find((error) => error);

                    if (firstError) {
                        message = firstError;
                    }
                }

                toast.warning(
                    message,

                    {
                        title: "Status updated",
                    },
                );
                return;
            }

            toast.success(`${getEnrollmentName(statusDialog)} is now ${newStatus}.`, {
                title: "Status updated",
            });

            await getEnrollments({
                requestedSkip: page.skip,
                requestedTake: page.take,
                requestedSearchText: searchText,
                requestedStatus: filterStatus,
                requestedSort: sort,
            });

            closeStatusDialog();
        } catch (err) {
            console.error("EFT status update error:", err);

            const res = err?.response?.data;

            const errors = res?.Data?.errors;
            let message = res?.Message || "Validation failed";

            if (errors) {
                const firstError = Object.values(errors)
                    .flat()
                    .find((error) => error);

                if (firstError) {
                    message = firstError;
                }
            }

            toast.warning(message, {
                title: "EFT Enrollment Status Change",
            });
        } finally {
            setStatusSaving(false);
        }
    };

    /* DEtails dialog */

    const openDetails = (enrollment) => {
        setSelectedEnrollment(enrollment);
        setShowDetailsDialog(true);
    };

    const closeDetails = () => {
        setSelectedEnrollment(null);
        setShowDetailsDialog(false);
    };

    /* 
         STATUS HISTORY
      */

    const openHistoryDialog = async (enrollment) => {
        setHistoryDialog(enrollment);
        setHistory([]);
        setHistoryLoading(true);

        try {
            const id = enrollment.eftEnrollmentId || enrollment.id;

            const response = await eftEnrollmentApi.getStatusHistory(id);

            const data = response?.data ?? response?.Data ?? [];

            setHistory(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("History error:", err);

            toast.error(err.message || "Failed to load status history.", {
                title: "History unavailable",
            });
        } finally {
            setHistoryLoading(false);
        }
    };

    const closeHistoryDialog = () => {
        if (historyLoading) return;

        setHistoryDialog(null);
        setHistory([]);
    };

    /*
         SECURE ACCOUNT NUMBER
      */

    const revealAccountNumber = async (enrollment) => {
        const id = enrollment.eftEnrollmentId || enrollment.id;

        console.log("REVEAL ACCOUNT NUMBER : " + JSON.stringify(enrollment));

        try {
            setEnrollments((prev) =>
                prev.map((item) =>
                    item.id === id
                        ? {
                            ...item,
                            rawAccountNumber: enrollment.accountNumber,
                        }
                        : item,
                ),
            );

            return true;
        } catch (err) {
            console.error("Account number error:", err);

            toast.error(err.message || "Unable to retrieve account number.", {
                title: "Access denied",
            });

            return false;
        }
    };

    /* 
         TABLE CELLS
      */

    const ProviderCell = (props) => {
        const item = props.dataItem;

        return (
            <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 9,
                    }}
                >
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: 10,
                            background: "rgba(13,148,136,.08)",
                            color: "#0D9488",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <Building2 size={16} strokeWidth={2} />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0,
                        }}
                    >
                        <span
                            style={{
                                fontWeight: 700,
                                color: "#1A1D21",
                                fontSize: 13,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {getEnrollmentName(item)}
                        </span>

                        <span
                            style={{
                                fontSize: 10.5,
                                color: "#9CA3AF",
                                marginTop: 2,
                            }}
                        >
                            {/*Enrollment #{item.id}*/}
                            {item?.registeredUserName && item?.registeredEmail && (
                                <>
                                    @{item?.registeredUserName}{" "}
                                    {item?.registeredEmail}
                                </>
                            )}

                        </span>
                    </div>
                </div>
            </td>
        );
    };

    const EnrollmentIdCell = (props) => {
        return (
            <td
                {...props.tdProps}
                colSpan={props.colSpan}
                style={{
                    ...props.style,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                    color: "#475569",
                }}
            >
                {props.dataItem.eftEnrollmentId || "—"}
            </td>
        );
    };

    const ConfirmationCell = (props) => {
        return (
            <td
                {...props.tdProps}
                colSpan={props.colSpan}
                style={{
                    ...props.style,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                    color: "#475569",
                }}
            >
                {props.dataItem.confirmationNumber || "—"}
            </td>
        );
    };

    const TINCell = (props) => {
        return (
            <td
                {...props.tdProps}
                colSpan={props.colSpan}
                style={{
                    ...props.style,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                    color: "#475569",
                }}
            >
                {props.dataItem.tin || "—"}
            </td>
        );
    };

    const NPICell = (props) => {
        return (
            <td
                {...props.tdProps}
                colSpan={props.colSpan}
                style={{
                    ...props.style,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                    color: "#475569",
                }}
            >
                {props.dataItem.npi || "—"}
            </td>
        );
    };

    const ContactCell = (props) => {
        const item = props.dataItem;

        return (
            <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                    }}
                >
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#374151",
                        }}
                    >
                        {item.contactName || "—"}
                    </span>

                    <span
                        style={{
                            fontSize: 10.5,
                            color: "#9CA3AF",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {item.email || "—"}
                    </span>

                </div>
            </td>
        );
    };

    const BankNameCell = (props) => {
        return (
            <td
                {...props.tdProps}
                colSpan={props.colSpan}
                style={{
                    ...props.style,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                    color: "#475569",
                }}
            >
                {props.dataItem.bankName || "—"}
            </td>
        );
    };

    const BankAccountCell = (props) => {
        const item = props.dataItem;

        const [show, setShow] = useState(!!item.rawAccountNumber);

        const [accountLoading, setAccountLoading] = useState(false);

        const toggle = async () => {
            if (show) {
                setShow(false);
                return;
            }

            setAccountLoading(true);

            try {
                const success = await revealAccountNumber(item);

                if (success) {
                    setShow(true);
                }
            } finally {
                setAccountLoading(false);
            }
        };

        return (
            <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
                <AccountNumber
                    maskedAccountNumber={{
                        masked:
                            item.maskedAccountNumber || maskAccountNumber(item.accountNumber),
                        raw: item.rawAccountNumber,
                    }}
                    revealed={show}
                    onToggle={toggle}
                    loading={accountLoading}
                />
            </td>
        );
    };

    const StatusCell = (props) => (
        <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
            <StatusBadge status={props.dataItem.status} />
        </td>
    );

    const DateCell = (props) => (
        <td
            {...props.tdProps}
            colSpan={props.colSpan}
            style={{
                ...props.style,
                color: "#6B7280",
                fontSize: 12,
            }}
        >
            {formatDate(props.dataItem.submissionDate)}
        </td>
    );

    const EffectiveDateCell = (props) => (
        <td
            {...props.tdProps}
            colSpan={props.colSpan}
            style={{
                ...props.style,
                color: "#6B7280",
                fontSize: 12,
            }}
        >
            {formatDate(props.dataItem.effectiveDate)}
        </td>
    );

    const NoteCell = (props) => (
        <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
            <span
                title={props.dataItem.adminNote || ""}
                style={{
                    display: "block",
                    maxWidth: 220,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 12,
                    color: "#64748B",
                }}
            >
                {props.dataItem.adminNote || "—"}
            </span>
        </td>
    );

    const RegisteredUserCell = (props) => {
        const item = props.dataItem;

        const fullName = [item.registeredFirstName, item.registeredLastName]
            .filter(Boolean)
            .join(" ");

        return (
            <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                    }}
                >
                    {/* Username */}
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#374151",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {item.registeredUserName ? `@${item.registeredUserName}` : "—"}
                    </span>

                    {/* First Name + Last Name */}
                    <span
                        style={{
                            fontSize: 11,
                            color: "#64748B",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {fullName || "—"}
                    </span>

                    {/* Email */}
                    <span
                        title={item.registeredEmail || ""}
                        style={{
                            fontSize: 10.5,
                            color: "#9CA3AF",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {item.registeredEmail || "—"}
                    </span>
                </div>
            </td>
        );
    };

    const handleEftAction = (userId, mode, tin) => {
        //alert("tin : "+tin+"MODE : "+mode+"USER ID :"+userId);
        //return;
        // navigate(`/forms/eft-enrollment?id=${id}&mode=${mode}&uid=${userId}`);

        saveListState();
        navigate(`/forms/eft-enrollment/${userId}/${mode}/${tin}`);
    };

    const loadProviderAdmins = async (search = "") => {
        try {
            setProviderLoading(true);

            const response = await eftEnrollmentApi.getProviderAdmins({
                search: search,
                first: 0,
                max: 20,
            });

            //console.log(
            //    'PROVIDER ADMINS RESPONSE:',
            //    response
            //);

            if (response?.success) {
                const providerList = Array.isArray(response.data) ? response.data : [];

                const formattedProviders = providerList
                    .filter((provider) => provider?.Id)
                    .map((provider) => ({
                        id: String(provider.Id),

                        username: provider.Username || "",

                        firstName: provider.FirstName || "",

                        lastName: provider.LastName || "",

                        email: provider.Email || "",

                        displayName:
                            [provider.FirstName, provider.LastName]
                                .filter(Boolean)
                                .join(" ") ||
                            provider.Username ||
                            "Unknown Provider",
                    }));

                //console.log(
                //    'FORMATTED PROVIDERS:',
                //    formattedProviders
                //);

                setProviders(formattedProviders);
            } else {
                setProviders([]);
            }
        } catch (error) {
            console.error("Failed to load provider admins:", error);

            setProviders([]);
        } finally {
            setProviderLoading(false);
        }
    };

    const handleProviderContinue = () => {
        if (!selectedProvider?.id) {
            toast.warning("Please select a provider before continuing.", {
                title: "Provider required",
            });
            return;
        }

        console.log("Selected Provider:", selectedProvider);

        console.log("Selected Provider Keycloak ID:", selectedProvider.id);

        setShowProviderDialog(false);

        //navigate(
        //    `/forms/eft-enrollment?uid=${encodeURIComponent(
        //        selectedProvider.id
        //    )}&mode=newEnrollment`
        //);

        saveListState();
        navigate(`/forms/eft-enrollment/${selectedProvider.id}/newEnrollment`);
    };

    const onAddEnrollment = async () => {
        setSelectedProvider(null);

        setProviderSearch("");

        setShowProviderDialog(true);

        await loadProviderAdmins("");
    };

    //const searchTimeoutRef = useRef(null);

    const handleProviderFilterChange = (searchValue) => {
        setProviderSearch(searchValue);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            loadProviderAdmins(searchValue);
        }, 300);
    };

    const ActionsCell = (props) => {
        const item = props.dataItem;

        return (
            <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
                <div
                    style={{
                        display: "flex",
                        gap: 6,
                    }}
                >
                    {accessPermissions.canViewEFTEnrollments && (
                        <>
                            <button
                                onClick={() => openDetails(props.dataItem)}
                                title="View enrollment"
                                name="btnViewEFTEnrollmentDetailsGrid"
                                style={iconBtn("#0D9488")}
                            >
                                <Eye size={13} strokeWidth={2.5} />
                            </button>
                        </>
                    )}
                    {accessPermissions.canEditEFTEnrollment && (
                        <>
                            <button
                                onClick={() => {
                                    handleEftAction(
                                        props.dataItem.userId,
                                        "change",
                                        props.dataItem.tin,
                                    );
                                }}
                                title="Edit enrollment"
                                name ="btnEditEFTEnrollmentGrid"
                                style={iconBtn("#2563EB")}
                            >
                                <Edit2 size={13} strokeWidth={2.5} />
                            </button>
                        </>
                    )}
                    {accessPermissions.canViewEFTStatusHistory && (
                        <>
                            <button
                                type="button"
                                onClick={() => openHistoryDialog(item)}
                                title="View status history"
                                name="btnViewStatusHistoryGrid"
                                style={iconBtn("#7C3AED")}
                            >
                                <History size={13} strokeWidth={2.5} />
                            </button>
                        </>
                    )}
                    {accessPermissions.canUpdateEFTStatus && (
                        <>
                            <button
                                type="button"
                                onClick={() => openStatusDialog(item)}
                                title="Change enrollment status"
                                name="btnChangeEnrollmentStatusGrid"
                                style={iconBtn("#0D9488")}
                            >
                                <RefreshCw size={13} strokeWidth={2.5} />
                            </button>
                        </>
                    )}

                </div>
            </td>
        );
    };

    /* 
         RENDER
       */

    return (
        <div className="main-content">
            {pageLoading && (
                <div style={styles.overlay}>
                    <LoadingSpinner message="Please wait..." fullPage="true" />
                </div>
            )}

            {/*
                HEADER
            */}

            <div className="page-header">
                <div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginBottom: 4,
                        }}
                    >
                        <Landmark size={22} color="#0D9488" strokeWidth={2} />

                        <h1 style={{ margin: 0 }}>EFT Enrollment</h1>
                    </div>

                    <p>
                        Manage provider electronic funds transfer enrollments, banking
                        information, and enrollment status
                    </p>
                </div>
            </div>

            {/* 
                STATUS CARDS
            */}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
                    gap: 12,
                    marginBottom: 20,
                }}
            >
                {STATUS_OPTIONS.map((status) => (
                    <StatusCountCard
                        key={status}
                        label={status}
                        status={status}
                        count={statusCounts[status] || 0}
                        active={filterStatus === status}
                        //onClick={() => {
                        //    setFilterStatus(filterStatus === status ? "All" : status);
                        //    setSearchText(status);

                        //}}

                        onClick={() => {
                            const newStatus = filterStatus === status ? "All" : status;

                            setFilterStatus(newStatus);

                            setPage((prev) => ({
                                ...prev,
                                skip: 0,
                            }));

                            getEnrollments({
                                requestedSkip: 0,
                                requestedTake: page.take,
                                requestedSearchText: searchText,
                                requestedStatus: newStatus,
                                requestedSort: sort,
                            });
                        }}
                    />
                ))}
            </div>

            <div style={{ position: "relative" }}>
                {gridLoading && (
                    <div style={styles.gridOverlay}>
                        <LoadingSpinner
                            data-cy="grid-loader"
                            message="Loading EFT Enrollments..."
                        />
                    </div>
                )}

                <div
                    className="card"
                    style={{
                        marginBottom: 20,
                    }}
                >
                    {/* Add Enrollment Button */}
                    <div
                        style={{
                            display: "flex",
                            width: "100%",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            marginBottom: 16,
                        }}
                    >
                        {accessPermissions.canCreateEFTEnrollment && (
                            <>
                                <button
                                    type="button"
                                    name="btnAddNewEnrollment"
                                    onClick={onAddEnrollment}
                                    style={{
                                        padding: "9px 16px",
                                        borderRadius: 8,
                                        border: "none",
                                        background: "#0D9488",
                                        color: "#fff",
                                        fontSize: 13,
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 7,
                                        fontFamily: "inherit",
                                        boxShadow: "0 2px 5px rgba(13, 148, 136, 0.25)",
                                    }}
                                >
                                    <Plus size={15} strokeWidth={2.5} />
                                    Add new Enrollment
                                </button>
                            </>
                        )}

                    </div>

                    {/* Search / Status Filter / View Toggle */}
                    <div
                        style={{
                            display: "flex",
                            gap: 12,
                            alignItems: "center",
                            flexWrap: "wrap",
                            width: "100%",
                        }}
                    >
                        {/* Search */}
                        <div
                            style={{
                                position: "relative",
                                flex: "1 1 300px",
                                minWidth: 220,
                            }}
                        >
                            <span
                                style={{
                                    position: "absolute",
                                    left: 14,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#9CA3AF",
                                    display: "flex",
                                }}
                            >
                                <Search size={16} />
                            </span>

                            <input
                                type="search"
                                name="selStatus"
                                value={searchText}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                placeholder="Search provider, TIN, NPI, email, bank..."
                                style={{
                                    width: "100%",
                                    padding: "10px 14px 10px 40px",
                                    borderRadius: 10,
                                    border: "1.5px solid #E8ECF0",
                                    fontSize: 14,
                                    fontFamily: "inherit",
                                    background: "#F8FAFB",
                                    outline: "none",
                                    boxSizing: "border-box",
                                }}
                            />
                        </div>

                        {/* Status Filter */}
                        <div
                            style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                            }}
                        >
                            <Filter size={15} color="#9CA3AF" />

                            <select
                                value={filterStatus}
                                /*onChange={(e) => {*/
                                /*    setFilterStatus(e.target.value);                                  */
                                /*    setSearchText(e.target.value);*/
                                /*}}*/
                                onChange={(e) => {
                                    const newStatus = e.target.value;

                                    setFilterStatus(newStatus);

                                    setPage((prev) => ({
                                        ...prev,
                                        skip: 0,
                                    }));

                                    getEnrollments({
                                        requestedSkip: 0,
                                        requestedTake: page.take,
                                        requestedSearchText: searchText,
                                        requestedStatus: newStatus,
                                        requestedSort: sort,
                                    });
                                }}
                                style={selectStyle}
                            >
                                <option value="All">All Statuses</option>

                                {STATUS_OPTIONS.map((status) => (
                                    <option key={status} value={status}>
                                        {status}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* View Toggle */}
                        <div
                            style={{
                                display: "flex",
                                background: "#F1F5F9",
                                borderRadius: 10,
                                padding: 3,
                                gap: 2,
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => setViewMode("cards")}
                                style={toggleBtnStyle(viewMode === "cards")}
                            >
                                <LayoutGrid size={14} strokeWidth={2.5} />
                                Cards
                            </button>

                            <button
                                type="button"
                                onClick={() => setViewMode("table")}
                                style={toggleBtnStyle(viewMode === "table")}
                            >
                                <List size={14} strokeWidth={2.5} />
                                Table
                            </button>
                        </div>
                    </div>

                    {/* Filter Summary */}
                    {/* ============================================================
            FILTER SUMMARY
        ============================================================ */}

                    {/*{(searchText || filterStatus !== "All") && (*/}
                        <div
                            style={{
                                marginTop: 12,
                                paddingTop: 12,
                                borderTop: "1px solid #F1F5F9",
                                fontSize: 13,
                                color: "#6B7280",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                            }}
                        >
                            <span>
                                Showing{" "}
                                <strong
                                    style={{
                                        color: "#1A1D21",
                                    }}
                                >
                                    {displayedEnrollments.length}
                                </strong>{" "}
                                of{" "}
                                <strong
                                    style={{
                                        color: "#1A1D21",
                                    }}
                                >
                                    {totalRecord}
                                </strong>{" "}
                                enrollments
                            </span>
                        {(searchText || filterStatus !== "All") && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchText("");
                                    setFilterStatus("All");

                                    sessionStorage.removeItem(EFT_LIST_STATE_KEY);

                                    if (searchTimeoutRef.current) {
                                        clearTimeout(searchTimeoutRef.current);
                                    }

                                    setPage((prev) => ({
                                        ...prev,
                                        skip: 0,
                                    }));

                                    getEnrollments({
                                        requestedSkip: 0,
                                        requestedTake: page.take,
                                        requestedSearchText: "",
                                        requestedStatus: "All",
                                        requestedSort: sort,
                                    });
                                }}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#0D9488",
                                    fontWeight: 600,
                                    fontSize: 13,
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    padding: 0,
                                }}
                            >
                                Clear filters
                            </button>
                        )}
                        </div>
                    {/*)}*/}
                </div>

                {error && (
                    <ErrorCard
                        message={error}
                        onRetry={() =>
                            getEnrollments({
                                requestedSkip: page.skip,
                                requestedTake: page.take,
                                requestedSearchText: searchText,
                                requestedSort: sort,
                            })
                        }
                    />
                )}

                {!loading && !error && displayedEnrollments.length === 0 && (
                    <EmptyState hasFilters={!!searchText || filterStatus !== "All"} />
                )}

                {/*CARDS VIEW*/}

                {!loading &&
                    !error &&
                    displayedEnrollments.length > 0 &&
                    viewMode === "cards" && (
                    <>
                        
                        <div
                            
                                style={{
                                    display: "grid",
                                    position: "relative",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                                    gap: 16,
                                
                                }}
                            >
                                {displayedEnrollments.map((enrollment) => (
                                    <EnrollmentCard
                                        key={enrollment.id}
                                        enrollment={enrollment}
                                        onChangeStatus={openStatusDialog}
                                        onHistory={openHistoryDialog}
                                        onRevealAccount={revealAccountNumber}
                                        onView={openDetails}
                                        onEdit={handleEftAction}
                                        accessPermissions={accessPermissions}
                                    />
                                ))}
                            </div>

                            {/* SERVER-SIDE PAGINATION */}

                            <CardsPagination
                                skip={page.skip}
                                take={page.take}
                                total={totalRecord}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}

                {/*TABLE VIEW*/}

                {!loading &&
                    !error &&
                    displayedEnrollments.length > 0 &&
                    viewMode === "table" && (
                        <div
                            style={{
                                overflow: "hidden",
                                borderRadius: 16,
                            }}
                        >
                            <div className="grid-scroll-wrapper">
                                <Grid
                                    style={{
                                        height: "560px",
                                    }}

                                    /*
                                     * IMPORTANT:
                                     * data is now ONLY the current API page.
                                     *
                                     * Do not use process().
                                     */
                                    data={enrollments}

                                    skip={page.skip}
                                    take={page.take}

                                    /*
                                     * IMPORTANT:
                                     * Total comes from SQL Server/API.
                                     */
                                    total={totalRecord}

                                    pageable={{
                                        buttonCount: 5,
                                        pageSizes: [5, 10, 20, 50],
                                        info: true,
                                        type: "numeric",
                                    }}

                                    sortable={{
                                        allowUnsort: true,
                                        mode: "single",
                                    }}

                                    resizable

                                    sort={sort}

                                    onPageChange={handlePageChange}
                                    onSortChange={handleSortChange}
                                >
                                    <GridColumn
                                        field="legalName"
                                        title="Provider"
                                        width="260px"
                                        cells={{
                                            data: ProviderCell,
                                        }}
                                    />

                                    <GridColumn
                                        field="eFTEnrollmentId"
                                        title="Enrollment Id"
                                        width="150px"
                                        cells={{
                                            data: EnrollmentIdCell,
                                        }}
                                    />

                                    <GridColumn
                                        field="confirmationNumber"
                                        title="Confirmation No."
                                        width="200px"
                                        cells={{
                                            data: ConfirmationCell,
                                        }}
                                    />

                                    <GridColumn
                                        field="tin"
                                        title="TIN"
                                        width="130px"
                                        cells={{
                                            data: TINCell,
                                        }}
                                    />

                                    <GridColumn
                                        field="npi"
                                        title="NPI"
                                        width="130px"
                                        cells={{
                                            data: NPICell,
                                        }}
                                    />

                                    <GridColumn
                                        field="contactName"
                                        title="Contact"
                                        width="200px"
                                        cells={{
                                            data: ContactCell,
                                        }}
                                    />

                                    <GridColumn
                                        field="bankName"
                                        title="Bank"
                                        cells={{
                                            data: BankNameCell,
                                        }}
                                        width="170px"
                                    />

                                    <GridColumn
                                        field="accountNumber"
                                        title="Account Number"
                                        width="165px"
                                        cells={{
                                            data: BankAccountCell,
                                        }}
                                        sortable={false}
                                    />

                                    <GridColumn
                                        field="status"
                                        title="Status"
                                        width="170px"
                                        cells={{
                                            data: StatusCell,
                                        }}
                                    />

                                    <GridColumn
                                        field="submissionDate"
                                        title="Submission Date"
                                        width="150px"
                                        cells={{
                                            data: DateCell,
                                        }}
                                    />

                                    <GridColumn
                                        field="effectiveDate"
                                        title="Effective Date"
                                        width="150px"
                                        cells={{
                                            data: EffectiveDateCell,
                                        }}
                                    />

                                    <GridColumn
                                        field="adminNote"
                                        title="Admin Note"
                                        width="230px"
                                        cells={{
                                            data: NoteCell,
                                        }}
                                    />

                                    {/*<GridColumn*/}
                                    {/*  field="registeredUserInfo"*/}
                                    {/*  title="User"*/}
                                    {/*  width="230px"*/}
                                    {/*  cells={{*/}
                                    {/*    data: RegisteredUserCell,*/}
                                    {/*  }}*/}
                                    {/*/>*/}

                                    <GridColumn
                                        title="Actions"
                                        width="150px"
                                        cells={{
                                            data: ActionsCell,
                                        }}
                                    />
                                </Grid>
                            </div>
                        </div>
                    )}
            </div>

            {/*CHANGE STATUS DIALOG*/}

            {statusDialog && (
                <Dialog
                    title="Change EFT Enrollment Status"
                    onClose={closeStatusDialog}
                    width={viewportWidth < 640 ? Math.max(viewportWidth - 24, 300) : 520}
                >
                    <div
                        style={{
                            padding: "8px 0",
                        }}
                    >
                        {/* Provider summary */}

                        <div
                            style={{
                                padding: 14,
                                borderRadius: 12,
                                background: "rgba(13,148,136,.05)",
                                border: "1px solid rgba(13,148,136,.12)",
                                marginBottom: 18,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: "#1A1D21",
                                }}
                            >
                                {getEnrollmentName(statusDialog)}
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "center",
                                    marginTop: 7,
                                    flexWrap: "wrap",
                                }}
                            >
                                <StatusBadge status={statusDialog.status} />

                                <span
                                    style={{
                                        fontSize: 11,
                                        color: "#9CA3AF",
                                        fontFamily: "monospace",
                                    }}
                                >
                                    Enrollment #{statusDialog.id}
                                </span>
                            </div>
                        </div>

                        {/* Status */}

                        <div
                            className="form-group"
                            style={{
                                marginBottom: 18,
                            }}
                        >
                            <label
                                style={{
                                    display: "block",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#6B7280",
                                    marginBottom: 7,
                                }}
                            >
                                New Status
                            </label>

                            <select
                                value={newStatus}
                                data-cy="enrollmentStatusSelect"
                                onChange={(e) => setNewStatus(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    border: "1.5px solid #E8ECF0",
                                    borderRadius: 10,
                                    background: "#F8FAFB",
                                    fontSize: 14,
                                    fontFamily: "inherit",
                                    outline: "none",
                                }}
                            >
                                {STATUS_OPTIONS.map((status) => (
                                    <option key={status} value={status}>
                                        {status}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Notes */}

                        <div className="form-group">
                            <label
                                style={{
                                    display: "block",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#6B7280",
                                    marginBottom: 7,
                                }}
                            >
                                Admin Notes
                            </label>

                            <textarea
                                value={statusNotes}
                                data-cy="enrollmentStatusNotes"
                                onChange={(e) => setStatusNotes(e.target.value)}
                                rows={5}
                                maxLength={1000}
                                placeholder="Enter reason, review notes, or additional information..."
                                style={{
                                    width: "100%",
                                    resize: "vertical",
                                    padding: "10px 12px",
                                    border: "1.5px solid #E8ECF0",
                                    borderRadius: 10,
                                    background: "#F8FAFB",
                                    fontSize: 14,
                                    fontFamily: "inherit",
                                    outline: "none",
                                    boxSizing: "border-box",
                                }}
                            />

                            <div
                                style={{
                                    textAlign: "right",
                                    fontSize: 11,
                                    color: "#9CA3AF",
                                    marginTop: 5,
                                }}
                            >
                                {statusNotes.length}/1000
                            </div>
                        </div>
                    </div>

                    <DialogActionsBar>
                        <Button onClick={closeStatusDialog} disabled={statusSaving} data-cy="enrollmentStatusChangeCloseButton">
                            Cancel
                        </Button>

                        <Button
                            data-cy="enrollmentStatusUpdateButton"
                            themeColor="primary"
                            onClick={handleStatusSave}
                            disabled={statusSaving || !newStatus}
                        >
                            {statusSaving ? "Updating..." : "Update Status"}
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {/*Add Enrollment Dialog*/}
            {showProviderDialog && (
                <Dialog
                    title="Add EFT Enrollment"
                    onClose={() => setShowProviderDialog(false)}
                    /*width={500}*/
                    width="min(500px, 95vw)"
                >
                    <div
                        style={{
                            padding: "8px 0",
                        }}
                    >
                        <div
                            style={{
                                fontSize: 13,
                                color: "#6B7280",
                                marginBottom: 12,
                            }}
                        >
                            Select a Provider Admin to create an EFT enrollment.
                        </div>

                        <label
                            style={{
                                display: "block",
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#374151",
                                marginBottom: 7,
                            }}
                        >
                            Provider Admin
                        </label>

                        <DropDownList
                            data={providers}
                            dataItemKey="id"
                            textField="displayName"
                            value={selectedProvider}
                            filterable={true}
                            filter={providerSearch}
                            loading={providerLoading}
                            name="providerAdminDropDown"

                            onFilterChange={(event) => {
                                const searchValue = event.filter?.value || "";

                                handleProviderFilterChange(searchValue);
                            }}

                            onChange={(event) => {
                                setSelectedProvider(event.value);
                            }}

                            placeholder="Search or select Provider Admin"

                            itemRender={(li, itemProps) => {
                                const provider = itemProps.dataItem;

                                const fullName = [provider?.firstName, provider?.lastName]
                                    .filter(Boolean)
                                    .join(" ");

                                return React.cloneElement(
                                    li,
                                    li.props,

                                    <div
                                        style={{
                                            padding: "6px 2px",
                                        }}
                                    >
                                        {/* Name */}
                                        <div
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 600,
                                                color: "#1F2937",
                                                marginBottom: 4,
                                            }}
                                        >
                                            {fullName || provider?.username || "Unknown Provider"}
                                        </div>

                                        {/* Email */}
                                        {provider?.email && (
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: "#6B7280",
                                                    marginBottom: 2,
                                                }}
                                            >
                                                {provider.email}
                                            </div>
                                        )}

                                        {/* Username */}
                                        {provider?.username && (
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: "#9CA3AF",
                                                }}
                                            >
                                                Username: {provider.username}
                                            </div>
                                        )}
                                    </div>,
                                );
                            }}

                            valueRender={(element, value) => {
                                if (!value) {
                                    return element;
                                }

                                const provider = value;

                                const fullName = [provider?.firstName, provider?.lastName]
                                    .filter(Boolean)
                                    .join(" ");

                                return React.cloneElement(
                                    element,
                                    element.props,

                                    <span>
                                        {fullName || provider?.username || "Select Provider Admin"}
                                    </span>,
                                );
                            }}

                            style={{
                                width: "100%",
                            }}
                        />

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 10,
                                marginTop: 24,
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => setShowProviderDialog(false)}
                                style={{
                                    padding: "9px 16px",
                                    borderRadius: 8,
                                    border: "1px solid #D1D5DB",
                                    background: "#fff",
                                    color: "#374151",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                disabled={!selectedProvider}
                                onClick={handleProviderContinue}
                                style={{
                                    padding: "9px 16px",
                                    borderRadius: 8,
                                    border: "none",
                                    background: selectedProvider ? "#0D9488" : "#CBD5E1",
                                    color: "#fff",
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: selectedProvider ? "pointer" : "not-allowed",
                                }}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </Dialog>
            )}

            {/*View Enrollment Details Dialog*/}

            {showDetailsDialog && selectedEnrollment && (
                <Dialog
                    title="EFT Enrollment Details"
                    onClose={closeDetails}
                    width="min(700px, 95vw)"
                >
                    <div
                        style={{
                            padding: "8px 0",
                            maxHeight: "calc(90vh - 120px)",
                            overflowY: "auto",
                            overflowX: "hidden",
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                                paddingBottom: 18,
                                marginBottom: 18,
                                borderBottom: "1px solid #E8ECF0",
                                flexWrap: "wrap",
                            }}
                        >
                            <ProviderAvatar name={selectedEnrollment.legalName} size={52} />

                            <div
                                style={{
                                    flex: "1 1 200px",
                                    minWidth: 0,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 17,
                                        fontWeight: 700,
                                        color: "#1A1D21",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {selectedEnrollment.legalName}
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        marginTop: 5,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: 12,
                                            color: "#6B7280",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {selectedEnrollment.confirmationNumber}
                                    </span>

                                    <StatusPill status={selectedEnrollment.status} />
                                </div>
                            </div>
                        </div>

                        {/* Provider Information */}
                        <DetailSection title="Provider Information" icon={Building2}>
                            <DetailItem
                                label="Legal Name"
                                value={selectedEnrollment.legalName}
                            />

                            <DetailItem label="DBA" value={selectedEnrollment.dba} />

                            <DetailItem label="TIN" value={selectedEnrollment.tin} mono />

                            <DetailItem label="NPI" value={selectedEnrollment.npi} mono />

                            <DetailItem
                                label="Contact"
                                value={selectedEnrollment.contactName}
                            />

                            <DetailItem label="Email" value={selectedEnrollment.email} />

                            <DetailItem
                                label="Telephone"
                                value={selectedEnrollment.telephone}
                            />

                            <DetailItem
                                label="Trading Partner ID"
                                value={selectedEnrollment.tradingPartnerId}
                            />
                        </DetailSection>

                        {/* Banking */}
                        <DetailSection title="Financial Institution" icon={CreditCard}>
                            <DetailItem
                                label="Bank Name"
                                value={selectedEnrollment.bankName}
                            />

                            <DetailItem
                                label="Routing Number"
                                value={selectedEnrollment.routingNumber}
                                mono
                            />

                            <DetailItem
                                label="Account Type"
                                value={selectedEnrollment.accountType}
                            />

                            <DetailItem
                                label="Account Number"
                                value={selectedEnrollment.accountNumber}
                                mono
                            />

                            <DetailItem
                                label="Linked To"
                                value={selectedEnrollment.linkAccountTo}
                            />

                            <DetailItem
                                label="Bank Contact"
                                value={selectedEnrollment.bankContactPhone}
                            />
                        </DetailSection>

                        {/* Authorization */}
                        <DetailSection title="Submission & Authorization" icon={FileText}>
                            <DetailItem label="Reason" value={selectedEnrollment.reason} />

                            <DetailItem
                                label="Effective Date"
                                value={formatDate(selectedEnrollment.effectiveDate)}
                            />

                            <DetailItem
                                label="Authorized Signer"
                                value={selectedEnrollment.authorizedSigner}
                            />

                            <DetailItem label="Title" value={selectedEnrollment.title} />

                            <DetailItem
                                label="Signature Date"
                                value={formatDate(selectedEnrollment.signatureDate)}
                            />

                            <DetailItem
                                label="Submitted At"
                                value={formatDateTime(selectedEnrollment.createdDate)}
                            />

                            <DetailItem
                                label="Admin Note"
                                value={selectedEnrollment.adminNote}
                            />
                        </DetailSection>

                        {/* Notes */}
                        {selectedEnrollment.notes && (
                            <div
                                style={{
                                    marginTop: 16,
                                    padding: 12,
                                    background: "#F8FAFB",
                                    borderRadius: 10,
                                    overflowWrap: "anywhere",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#6B7280",
                                        marginBottom: 5,
                                    }}
                                >
                                    Notes
                                </div>

                                <div
                                    style={{
                                        fontSize: 13,
                                        color: "#1A1D21",
                                        lineHeight: 1.5,
                                        whiteSpace: "pre-wrap",
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {selectedEnrollment.notes}
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogActionsBar>
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 8,
                                flexWrap: "wrap",
                            }}
                        >
                            <button
                                type="button"
                                onClick={closeDetails}
                                data-cy="close-enrollment-details-button"

                                style={{
                                    padding: "8px 16px",
                                    borderRadius: 8,
                                    border: "1px solid #E8ECF0",
                                    background: "#fff",
                                    color: "#6B7280",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                }}
                            >
                                Close
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    handleEftAction(
                                        selectedEnrollment.userId,
                                        "change",
                                        selectedEnrollment.tin,
                                    );
                                }}
                                style={{
                                    padding: "8px 16px",
                                    borderRadius: 8,
                                    border: "none",
                                    background: "linear-gradient(135deg, #0D9488, #0F766E)",
                                    color: "#fff",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <Edit2
                                    size={13}
                                    style={{
                                        marginRight: 5,
                                    }}
                                />
                                Edit
                            </button>
                        </div>
                    </DialogActionsBar>
                </Dialog>
            )}

            {/* 
                STATUS HISTORY DIALOG
            */}

            {historyDialog && (
                <Dialog
                    title="EFT Enrollment Status History"
                    onClose={closeHistoryDialog}
                    width={viewportWidth < 640 ? Math.max(viewportWidth - 24, 300) : 680}
                >
                    <div
                        style={{
                            padding: "4px 0",
                            maxHeight: 520,
                            overflowY: "auto",
                        }}
                    >
                        {/* Enrollment Header */}

                        <div
                            style={{
                                padding: 14,
                                borderRadius: 12,
                                background: "#F8FAFC",
                                border: "1px solid #E8ECF0",
                                marginBottom: 20,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: "#1A1D21",
                                }}
                            >
                                {getEnrollmentName(historyDialog)}
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    gap: 14,
                                    flexWrap: "wrap",
                                    marginTop: 7,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 11,
                                        color: "#6B7280",
                                    }}
                                >
                                    Enrollment #{historyDialog.id}
                                </span>

                                <span
                                    style={{
                                        fontSize: 11,
                                        color: "#6B7280",
                                    }}
                                >
                                    TIN: <strong>{historyDialog.tin || "—"}</strong>
                                </span>

                                <span
                                    style={{
                                        fontSize: 11,
                                        color: "#6B7280",
                                    }}
                                >
                                    NPI: <strong>{historyDialog.npi || "—"}</strong>
                                </span>
                            </div>
                        </div>

                        {historyLoading && (
                            <div
                                style={{
                                    padding: 40,
                                    textAlign: "center",
                                }}
                            >
                                <LoadingSpinner message="Loading status history..." />
                            </div>
                        )}

                        {!historyLoading && history.length === 0 && (
                            <div
                                style={{
                                    padding: 40,
                                    textAlign: "center",
                                    color: "#9CA3AF",
                                    fontSize: 13,
                                }}
                            >
                                No status history found.
                            </div>
                        )}

                        {!historyLoading && history.length > 0 && (
                            <div
                                style={{
                                    position: "relative",
                                    paddingLeft: 30,
                                }}
                            >
                                {/* Timeline line */}

                                <div
                                    style={{
                                        position: "absolute",
                                        left: 9,
                                        top: 8,
                                        bottom: 8,
                                        width: 2,
                                        background: "#E8ECF0",
                                    }}
                                />

                                {history.map((item, index) => {
                                    const status = item.Status ?? item.status ?? "";

                                    const changedBy = item.ChangedBy ?? item.changedBy ?? "";

                                    const changedDate =
                                        formatDateTime(item.ChangedDate) ??
                                        formatDateTime(item.changedDate) ??
                                        "";

                                    const notes = item.Notes ?? item.notes ?? "";

                                    const meta = STATUS_META[status] || {
                                        bg: "#F3F4F6",
                                        color: "#6B7280",
                                        icon: CircleAlert,
                                    };

                                    const Icon = meta.icon;

                                    return (
                                        <div
                                            key={
                                                item.EFTEnrollmentStatusAuditId ??
                                                item.eFTEnrollmentStatusAuditId ??
                                                index
                                            }
                                            style={{
                                                position: "relative",
                                                paddingBottom: index === history.length - 1 ? 0 : 22,
                                            }}
                                        >
                                            {/* Timeline icon */}

                                            <div
                                                style={{
                                                    position: "absolute",
                                                    left: -30,
                                                    top: 0,
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    background: meta.bg,
                                                    color: meta.color,
                                                    border: "2px solid #fff",
                                                    boxShadow: "0 0 0 1px #E8ECF0",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    zIndex: 1,
                                                }}
                                            >
                                                <Icon size={10} strokeWidth={2.5} />
                                            </div>

                                            <div
                                                style={{
                                                    padding: "11px 13px",
                                                    border: "1px solid #E8ECF0",
                                                    borderRadius: 11,
                                                    background: "#fff",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 10,
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <StatusBadge status={status} />

                                                    <span
                                                        style={{
                                                            fontSize: 10.5,
                                                            color: "#9CA3AF",
                                                        }}
                                                    >
                                                        {formatDateTime(changedDate)}
                                                    </span>
                                                </div>

                                                <div
                                                    style={{
                                                        marginTop: 8,
                                                        fontSize: 12,
                                                        color: "#475569",
                                                    }}
                                                >
                                                    <strong>Changed by:</strong> {changedBy || "System"}
                                                </div>

                                                {notes && (
                                                    <div
                                                        style={{
                                                            marginTop: 7,
                                                            padding: "8px 10px",
                                                            background: "#F8FAFC",
                                                            borderRadius: 8,
                                                            fontSize: 12,
                                                            color: "#64748B",
                                                            lineHeight: 1.45,
                                                        }}
                                                    >
                                                        {notes}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <DialogActionsBar>
                        <Button onClick={closeHistoryDialog} data-cy='close-history-dialog-button'>Close</Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
};

const DetailSection = ({ title, icon: Icon, children }) => (
    <div
        style={{
            marginBottom: 20,
        }}
    >
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                paddingBottom: 9,
                marginBottom: 12,
                borderBottom: "1px solid #E8ECF0",
            }}
        >
            <Icon size={16} color="#0D9488" />

            <span
                style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#1A1D21",
                }}
            >
                {title}
            </span>
        </div>

        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 14,
            }}
        >
            {children}
        </div>
    </div>
);

const DetailItem = ({ label, value, mono = false }) => (
    <div>
        <div
            style={{
                fontSize: 11,
                color: "#9CA3AF",
                marginBottom: 3,
            }}
        >
            {label}
        </div>

        <div
            style={{
                fontSize: 13,
                color: "#1A1D21",
                fontWeight: 600,
                fontFamily: mono ? "monospace" : "inherit",
                wordBreak: "break-word",
            }}
        >
            {value || "—"}
        </div>
    </div>
);
/* 
   EMPTY STATE
*/

const EmptyState = ({ hasFilters }) => (
    <div
        className="card"
        style={{
            textAlign: "center",
            padding: "56px 24px",
        }}
    >
        <div
            style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                background: "rgba(13,148,136,.08)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 16,
            }}
        >
            {hasFilters ? (
                <Search size={28} color="#0D9488" strokeWidth={2} />
            ) : (
                <Landmark size={28} color="#0D9488" strokeWidth={2} />
            )}
        </div>

        <div
            style={{
                fontSize: 17,
                fontWeight: 700,
                color: "#1A1D21",
                marginBottom: 6,
            }}
        >
            {hasFilters
                ? "No EFT enrollments match your filters"
                : "No EFT enrollments yet"}
        </div>

        <div
            style={{
                fontSize: 13,
                color: "#6B7280",
            }}
        >
            {hasFilters
                ? "Try clearing the filters or adjusting the search."
                : "EFT enrollments will appear here when providers submit them."}
        </div>
    </div>
);

/* ============================================================
   STYLES
============================================================ */

const styles = {
    overlay: {
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(255,255,255,.6)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
    },
};

const iconBtn = (color) => ({
    width: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${color}33`,
    background: `${color}0A`,
    color,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
});

const smallActionButton = (color) => ({
    padding: "6px 10px",
    borderRadius: 8,
    border: `1px solid ${color}33`,
    background: `${color}0A`,
    color,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontFamily: "inherit",
});

const selectStyle = {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1.5px solid #E8ECF0",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    background: "#F8FAFB",
    cursor: "pointer",
    fontFamily: "inherit",
    outline: "none",
};

const toggleBtnStyle = (active) => ({
    padding: "7px 12px",
    borderRadius: 8,
    border: "none",
    background: active ? "#fff" : "transparent",
    color: active ? "#0D9488" : "#6B7280",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    boxShadow: active ? "0 1px 3px rgba(0,0,0,.08)" : "none",
    fontFamily: "inherit",
    transition: "all .15s",
});

export default EFTEnrollmentListPage;
